import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { Formik, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import * as api from '~/Services/Service';

const validationSchema = Yup.object({
  codigoUsuario: Yup.number().required('Selecione um usuário'),
  codigoGrupoEmpresa: Yup.number().required('Selecione um G.E.'),
  contaPortador: Yup.number().required('Selecione um portador'),
});

const initialValues = {
  codigoUsuario: '',
  codigoGrupoEmpresa: '',
  contaPortador: '',
};

const ModalNovoPortador = ({ mostrar, onFechar, onSalvar, usuarioFixo, portadoresExistentes }) => {
  const [usuarios, setUsuarios] = useState([]);
  const [gruposEmpresa, setGruposEmpresa] = useState([]);
  const [contasPortador, setContasPortador] = useState([]);

  useEffect(() => {
    if (mostrar) {
      carregarDadosIniciais();
    }
  }, [mostrar]);

  const carregarDadosIniciais = async () => {
    try {
      const codigoExcluir = usuarioFixo ? usuarioFixo.value : null;
      const promises = [api.listarGruposEmpresaPortador(codigoExcluir)];
      if (!usuarioFixo) {
        promises.unshift(api.listarUsuariosAtivosPortador());
      }
      const results = await Promise.all(promises);
      if (!usuarioFixo) {
        setUsuarios(
          (results[0] || []).map(u => ({ value: u.codigo, label: `${u.codigo} - ${u.nome}` }))
        );
        setGruposEmpresa(
          (results[1] || []).map(ge => ({ value: ge.codigo, label: `${ge.codigo} - ${ge.apelido}` }))
        );
      } else {
        setGruposEmpresa(
          (results[0] || []).map(ge => ({ value: ge.codigo, label: `${ge.codigo} - ${ge.apelido}` }))
        );
      }
    } catch {
      toast.error('Erro ao carregar dados do formulário.');
    }
  };

  const obterPortadorSugerido = () => {
    if (!portadoresExistentes || portadoresExistentes.length === 0) return null;
    // Pega o portador mais frequente do usuário nos outros GEs
    var contagem = {};
    portadoresExistentes.forEach(p => {
      var chave = p.conta_portador;
      contagem[chave] = (contagem[chave] || 0) + 1;
    });
    var maisFrequente = null;
    var max = 0;
    Object.keys(contagem).forEach(chave => {
      if (contagem[chave] > max) {
        max = contagem[chave];
        maisFrequente = Number(chave);
      }
    });
    return maisFrequente;
  };

  const carregarContas = async (codigoGE, setFieldValue) => {
    setContasPortador([]);
    setFieldValue('contaPortador', '');
    if (!codigoGE) return;
    try {
      const resp = await api.listarContasPortador(codigoGE);
      const opcoes = (resp || []).map(c => ({ value: c.codigo, label: `${c.codigo} - ${c.apelido}` }));
      setContasPortador(opcoes);

      // Sugerir portador que o usuário já usa em outros GEs
      var sugerido = obterPortadorSugerido();
      if (sugerido && opcoes.some(o => o.value === sugerido)) {
        setFieldValue('contaPortador', sugerido);
      }
    } catch {
      toast.error('Erro ao carregar contas portador.');
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      await api.incluirPortadorUsuario({
        codigo_usuario: values.codigoUsuario,
        codigo_grupo_empresa: values.codigoGrupoEmpresa,
        conta_portador: values.contaPortador,
      });
      onSalvar();
    } catch (err) {
      toast.error((err && err.message) || 'Erro ao incluir vínculo.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFechar = (resetForm) => {
    setContasPortador([]);
    resetForm();
    onFechar();
  };

  var valoresIniciais = usuarioFixo
    ? { codigoUsuario: usuarioFixo.value, codigoGrupoEmpresa: '', contaPortador: '' }
    : initialValues;

  return (
    <Formik
      initialValues={valoresIniciais}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ values, errors, touched, setFieldValue, handleSubmit: formikSubmit, isSubmitting, resetForm }) => (
        <Modal show={mostrar} onHide={() => handleFechar(resetForm)} backdrop="static">
          <Modal.Header closeButton>
            <Modal.Title>Novo Vínculo Portador</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={formikSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Usuário <span className="text-danger">*</span></Form.Label>
                {usuarioFixo ? (
                  <Form.Control type="text" value={usuarioFixo.label} disabled />
                ) : (
                  <Select
                    placeholder="Selecione um usuário..."
                    options={usuarios}
                    value={usuarios.find(u => u.value === values.codigoUsuario) || null}
                    onChange={opt => setFieldValue('codigoUsuario', opt ? opt.value : '')}
                    components={{ DropdownIndicator }}
                    styles={customStyles}
                    isClearable
                  />
                )}
                {touched.codigoUsuario && errors.codigoUsuario && (
                  <div className="text-danger small mt-1">{errors.codigoUsuario}</div>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>G.E. <span className="text-danger">*</span></Form.Label>
                <Select
                  placeholder="Selecione um G.E...."
                  options={gruposEmpresa}
                  value={gruposEmpresa.find(ge => ge.value === values.codigoGrupoEmpresa) || null}
                  onChange={opt => {
                    setFieldValue('codigoGrupoEmpresa', opt ? opt.value : '');
                    carregarContas(opt ? opt.value : null, setFieldValue);
                  }}
                  components={{ DropdownIndicator }}
                  styles={customStyles}
                  isClearable
                />
                {touched.codigoGrupoEmpresa && errors.codigoGrupoEmpresa && (
                  <div className="text-danger small mt-1">{errors.codigoGrupoEmpresa}</div>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Portador <span className="text-danger">*</span></Form.Label>
                <Select
                  placeholder={values.codigoGrupoEmpresa ? 'Selecione um portador...' : 'Selecione um G.E. primeiro'}
                  options={contasPortador}
                  value={contasPortador.find(c => c.value === values.contaPortador) || null}
                  onChange={opt => setFieldValue('contaPortador', opt ? opt.value : '')}
                  isDisabled={!values.codigoGrupoEmpresa}
                  components={{ DropdownIndicator }}
                  styles={customStyles}
                  isClearable
                  noOptionsMessage={() => 'Nenhuma conta portador disponível para este G.E.'}
                />
                {touched.contaPortador && errors.contaPortador && (
                  <div className="text-danger small mt-1">{errors.contaPortador}</div>
                )}
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => handleFechar(resetForm)}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={formikSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Salvando...' : 'Salvar'}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </Formik>
  );
};

export default ModalNovoPortador;
